// src/components/dashboard/DashboardOnDeckTab.jsx
// ============================================================
// ON DECK TAB - Shows beers conditioning/lagering/carbonating
// ============================================================
// Similar to BeersTab but for beers not yet on tap.
// Uses blue color scheme to differentiate from on-tap beers.
// Sorts beers by configured tap order.
// ============================================================

import React from 'react';
import { ChevronDown, ChevronRight, MapPin /*, Star */ } from 'lucide-react';
import { BeerCard } from '../shared/BeerCard';
import { sortBeersByTapOrder } from '../../utils/filterUtils';

/**
 * DashboardOnDeckTab - Displays beers that are ready but not yet on tap
 */
export const DashboardOnDeckTab = ({ 
  locations,
  onDeckBeersByLocation, 
  expandedLocations, 
  toggleLocationExpansion,
  account,
  onOpenRatingModal, 
  onViewKeg 
}) => {
  if (onDeckBeersByLocation.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <p className="text-gray-500 dark:text-gray-400 text-center">
          No beers on deck. Fill some kegs to get them ready!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {locations.map(location => {
        const locationBeers = onDeckBeersByLocation.filter(g => g.locationId === location.id);
        if (locationBeers.length === 0) return null;

        const isExpanded = expandedLocations[location.id] !== undefined 
          ? expandedLocations[location.id] 
          : true;

        const totalBeerCount = locationBeers.reduce((acc, g) => acc + g.beers.length, 0);

        return (
          <div key={location.id} className="bg-white dark:bg-gray-800 rounded-lg shadow">
            {/* Header */}
            <div 
              className="bg-blue-600 text-white px-6 py-3 flex items-center justify-between cursor-pointer hover:bg-blue-700 transition"
              onClick={() => toggleLocationExpansion(location.id)}
            >
              <div className="flex items-center gap-2">
                {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                <MapPin className="w-5 h-5" />
                <h3 className="font-semibold text-lg">{location.name}</h3>
                <span className="text-sm text-blue-200">
                  ({totalBeerCount} beer{totalBeerCount !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
            
            {/* Content */}
            {isExpanded && (
              <div className="p-6 space-y-6">
                {locationBeers.map(group => {
                  // Sort beers by tap order
                  const sortedBeers = sortBeersByTapOrder(group.beers, [location], location.name, group.sublocation);
                  
                  return (
                    <div key={group.sublocationId}>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                        <span className="text-blue-600">→</span>
                        {group.sublocation}
                      </h4>
                      <div className="space-y-4">
                        {sortedBeers.map(keg => (
                          <BeerCard
                            key={keg.id}
                            keg={keg}
                            account={account}
                            onOpenRatingModal={onOpenRatingModal}
                            onViewKeg={onViewKeg}
                            borderColor="border-blue-500"
                            showStatus={true}
                            showTapNumber={false}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
export default DashboardOnDeckTab;
